// /app/frontend/src/pages/seo/MontgomeryDatingAppPage.jsx
// City landing page — Montgomery, Alabama.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function MontgomeryDatingAppPage() {
  const path = '/montgomery-dating-app';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Montgomery Dating App', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Montgomery Dating App"
        description="UnityLove connects intentional singles, couples, and families across Montgomery, Alabama and the surrounding River Region."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="Montgomery, Alabama"
        headline="Dating & Community In Montgomery"
        sub="A city whose history shaped America, and whose people are still building something meaningful. UnityLove brings intentional dating and real community to Montgomery and the River Region."
        body="From historic downtown to East Montgomery, Prattville to Wetumpka — connect with singles, couples, and families who share your values."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Browse All Guides', to: '/guides' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Why Montgomery */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Why Montgomery
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Montgomery is a city whose history is unavoidable — the birthplace of the modern Civil Rights Movement, the home of the Equal Justice Initiative, the city where Rosa Parks took her seat and Dr. King preached at Dexter Avenue Baptist. That history is not background. It shapes the way Montgomery loves, the way Montgomery raises families, and the way Montgomery values community.
            </p>
            <p>
              Outside the largest metros, dating online has often meant settling for apps that were not designed with smaller markets in mind — and definitely not for Black singles and families in the South. UnityLove changes the math. Verified, intentional members. Profile prompts that surface real compatibility. A community that takes the River Region seriously.
            </p>
          </div>
        </section>

        {/* Who's here */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            Who You&rsquo;ll Find In Montgomery
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🎓"
              accent="amber"
              title="HBCU Alumni"
              body="Alabama State University alumni and students — generations of Hornets who built lifelong networks in Montgomery and across the Black Belt."
            />
            <FeatureCard
              icon="⛪"
              accent="violet"
              title="Faith-Rooted Community"
              body="Montgomery is one of the most active Black church regions in the country. From historic Dexter Avenue to neighborhood congregations, faith plays a central role in dating and family-building."
            />
            <FeatureCard
              icon="👨‍👩‍👧‍👦"
              accent="rose"
              title="Family-Building Singles & Couples"
              body="The South has long valued long-term partnership and family. Montgomery daters tend to come ready for serious conversation about kids, household, and shared vision."
            />
            <FeatureCard
              icon="✊🏾"
              accent="emerald"
              title="Civic & Community Builders"
              body="A city where civic engagement is part of daily life — educators, organizers, healthcare workers, and entrepreneurs building Montgomery&rsquo;s next chapter."
            />
          </div>
        </section>

        {/* Around the city */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Around The River Region
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            Montgomery dating extends across the broader River Region — a community of small cities and neighborhoods tied together by family, faith, and history:
          </p>
          <ul className="grid gap-3 sm:grid-cols-2 text-slate-700">
            <li><strong className="text-slate-900">Downtown / Five Points</strong> — Historic core, civic life, walkable date spots</li>
            <li><strong className="text-slate-900">East Montgomery</strong> — Suburban families, professionals, growing community</li>
            <li><strong className="text-slate-900">West Montgomery</strong> — Historic Black neighborhoods, deep family roots</li>
            <li><strong className="text-slate-900">Prattville</strong> — Family-oriented community across the river</li>
            <li><strong className="text-slate-900">Wetumpka</strong> — Smaller-town pace, growing dating community</li>
            <li><strong className="text-slate-900">Pike Road / East ALU corridor</strong> — Newer development, established couples</li>
          </ul>
        </section>

        {/* What's different */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            What Makes Montgomery Dating Different
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              In smaller markets, dating apps from major cities often feel hollow — the pool is thin, the ghosting is constant, and casual culture rarely fits a community that takes family seriously. Montgomery and the River Region deserve better than the same apps repurposed.
            </p>
            <p>
              UnityLove&rsquo;s approach — verified members, intentional pacing, profile depth, themed conversation spaces — works especially well in smaller markets. When you know the people on the platform are real and serious, the entire experience changes. Quality over quantity is not just a slogan in a market this size; it is the only model that works.
            </p>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Continue Reading
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/black-love-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Love Dating App</p>
              <p className="text-sm text-slate-600 mt-1">Centering culture and connection.</p>
            </a>
            <a href="/atlanta-black-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Atlanta Black Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connections across the ATL.</p>
            </a>
            <a href="/houston-black-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Houston Black Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connections in H-Town.</p>
            </a>
            <a href="/dating-app-for-intentional-relationships" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating App For Intentional Relationships</p>
              <p className="text-sm text-slate-600 mt-1">Built for people who want something real.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
